import { useMemo } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './api/queryClient'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AppTooltipProvider } from './components/AppTooltip'
import { AppConfirmModalRoot } from './components/AppConfirmModal'
import { ProtectedRoute, PublicOnlyRoute } from './routes/ProtectedRoute'
import PermissionGuard from './components/PermissionGuard'
import DashboardLayout from './layouts/DashboardLayout'
import { useAuth } from './hooks/useAuth'
import { AppNotificationBusProvider } from './context/notifications/AppNotificationBus'
import GlobalCallPopup from './components/call/GlobalCallPopup'
import DetachedCallPopupWindow from './components/call/DetachedCallPopupWindow'

// Auth
import { LoginScreen } from './features/auth/login'
import { SystemVerificationScreen } from './features/auth/system-verification'
import { OTPVerificationScreen, PasswordResetScreen } from './features/auth/password-recovery'
import { AuthCallbackScreen } from './features/auth/callback'

// Dashboard
import DashboardScreen from './features/dashboard/DashboardScreen'

// Masters (Core)
import CompanyMasterListScreen from './features/masters/company/CompanyMasterListScreen'
import CompanyMasterEntryScreen from './features/masters/company/CompanyMasterEntryScreen'
import BranchMasterListScreen from './features/masters/branch/BranchMasterListScreen'
import BranchMasterEntryScreen from './features/masters/branch/BranchMasterEntryScreen'
import DepartmentMasterListScreen from './features/masters/department/DepartmentMasterListScreen'
import DepartmentMasterEntryScreen from './features/masters/department/DepartmentMasterEntryScreen'
import RoleRightsListScreen from './features/masters/roles/RoleRightsListScreen'
import RoleConfigurationScreen from './features/masters/roles/RoleConfigurationScreen'
import UserDirectoryScreen from './features/masters/users/UserDirectoryScreen'
import UserProfileManagementScreen from './features/masters/users/UserProfileManagementScreen'
import TaskMasterListScreen from './features/masters/task-master/TaskMasterListScreen'
import TaskMasterEntryScreen from './features/masters/task-master/TaskMasterEntryScreen'
import JobMasterListScreen from './features/masters/job-master/JobMasterListScreen'
import JobMasterEntryScreen from './features/masters/job-master/JobMasterEntryScreen'
import CurrencyMasterScreen from './features/masters/currency/CurrencyMasterListScreen'
import CurrencyMasterEntryScreen from './features/masters/currency/CurrencyMasterEntryScreen'
import SubscriptionSettingsScreen from './features/subscriptions/SubscriptionSettingsScreen'

// Masters (A/C Heads)
import AcHeadListScreen from './features/masters/acheads/heads/AcHeadListScreen'
import AcHeadEntryScreen from './features/masters/acheads/heads/AcHeadEntryScreen'
import { VendorListScreen, VendorEntryScreen } from './features/masters/acheads/vendor'
import { BankListScreen, BankEntryScreen } from './features/masters/acheads/bank'
import { EmployeeListScreen, EmployeeEntryScreen } from './features/masters/acheads/employee'
import { CustomerListScreen, CustomerEntryScreen } from './features/masters/acheads/customer'
import { CustCashListScreen, CustCashEntryScreen } from './features/masters/acheads/cust-cash'

// Masters (Academic & Education)
import CoursesListScreen from './features/masters/courses/CoursesListScreen'
import CoursesEntryScreen from './features/masters/courses/CoursesEntryScreen'
import BatchesListScreen from './features/masters/batches/BatchesListScreen'
import BatchesEntryScreen from './features/masters/batches/BatchesEntryScreen'
import SubjectsListScreen from './features/masters/subjects/SubjectsListScreen'
import SubjectsEntryScreen from './features/masters/subjects/SubjectsEntryScreen'
import SchemesListScreen from './features/masters/schemes/SchemesListScreen'
import SchemesEntryScreen from './features/masters/schemes/SchemesEntryScreen'
import ClassroomListScreen from './features/masters/classroom/ClassroomListScreen'
import ClassroomEntryScreen from './features/masters/classroom/ClassroomEntryScreen'
import BoardsListScreen from './features/masters/boards/BoardsListScreen'
import BoardsEntryScreen from './features/masters/boards/BoardsEntryScreen'
import MediumListScreen from './features/masters/medium/MediumListScreen'
import MediumEntryScreen from './features/masters/medium/MediumEntryScreen'
import SchoolsListScreen from './features/masters/schools/SchoolsListScreen'
import SchoolsEntryScreen from './features/masters/schools/SchoolsEntryScreen'
import CasteListScreen from './features/masters/caste/CasteListScreen'
import CasteEntryScreen from './features/masters/caste/CasteEntryScreen'
import SubCasteListScreen from './features/masters/sub-caste/SubCasteListScreen'
import SubCasteEntryScreen from './features/masters/sub-caste/SubCasteEntryScreen'
import OccupationListScreen from './features/masters/occupation/OccupationListScreen'
import OccupationEntryScreen from './features/masters/occupation/OccupationEntryScreen'
import RelationListScreen from './features/masters/relation/RelationListScreen'
import RelationEntryScreen from './features/masters/relation/RelationEntryScreen'
import SubjectFeesListScreen from './features/masters/subject-fees/SubjectFeesListScreen'
import SubjectFeesEntryScreen from './features/masters/subject-fees/SubjectFeesEntryScreen'
import { PlaceListScreen, PlaceEntryScreen } from './features/masters/place'
import { QualificationListScreen, QualificationEntryScreen } from './features/masters/qualification'
import { SourcesListScreen, SourcesEntryScreen } from './features/masters/sources'
import EmployeeDocumentMasterListScreen from './features/masters/employee-document-master/EmployeeDocumentMasterListScreen'
import EmployeeDocumentMasterEntryScreen from './features/masters/employee-document-master/EmployeeDocumentMasterEntryScreen'
import { RemarksCategoryListScreen, RemarksCategoryEntryScreen } from './features/masters/remarks-category'
import { ContactCategoryListScreen, ContactCategoryEntryScreen } from './features/masters/contact-category'
import { ReasonTypeListScreen, ReasonTypeEntryScreen } from './features/masters/reason-type'
import { InventoryGroupListScreen, InventoryGroupEntryScreen } from './features/masters/inventory-group'

import { BrandListScreen, BrandEntryScreen } from './features/masters/brand'
import { PaymentModeListScreen, PaymentModeEntryScreen } from './features/masters/payment-mode'
import { InventoryListScreen, InventoryEntryScreen } from './features/masters/inventory'
import { EmployeeGroupListScreen, EmployeeGroupEntryScreen } from './features/masters/employee-group'
import { HolidayMasterListScreen, HolidayMasterEntryScreen } from './features/masters/holiday-master'

// Payroll Suite
import {
  PayHeadsScreen,
  PayHeadsListScreen,
  PayHeadsEntryScreen,
  BiometricsScreen,
  ManualPunchScreen,
  ManualPunchListScreen,
  ManualPunchEntryScreen,
  ManualPunchBulkUploadScreen,
  RemoteAttandanceScreen,
  RemoteAttendanceListScreen,
  RemoteAttendanceEntryScreen,
  RemoteAttendanceBulkUploadScreen,
  ShiftMasterScreen,
  ShiftMasterListScreen,
  ShiftMasterEntryScreen,
  LeaveMasterScreen,
  LeaveMasterListScreen,
  LeaveMasterEntryScreen,
  LeaveRulesScreen,
  LeaveRulesListScreen,
  LeaveRulesEntryScreen,
  LeaveRulesDetailScreen,
  LeaveApplicationScreen,
  LeaveApplicationListScreen,
  LeaveApplicationEntryScreen,
  IncentivesScreen,
  IncentivesListScreen,
  IncentivesEntryScreen,
  DeductionsScreen,
  DeductionsListScreen,
  DeductionsEntryScreen,
  AttendanceRulesScreen,
  AttendanceRulesListScreen,
  AttendanceRulesEntryScreen,
  AttendanceHistoryListScreen,
  DailyProcessScreen,
  DailyProcessListScreen,
  DailyProcessRunScreen,
  MonthlyProcessScreen,
  MonthlyProcessListScreen,
  MonthlyProcessRunScreen,
  LoansScreen,
  LoansEntryScreen,
  SalaryProcessScreen,
  SalaryProcessEntryScreen,
  SettingsScreen as PayrollSettingsScreen,
} from './features/payroll'

// Masters (Generic Shared)
import SharedMasterPage from './features/masters/generic-master/SharedMasterPage'
import SharedMasterEntryScreen from './features/masters/generic-master/SharedMasterEntryScreen'

// Admission Module Suite
import {
  StudentsListScreen,
  StudentsEntryScreen,
  StudentAttendanceScreen,
  StudentBiometricScreen,
  StudentDocumentScreen,
  StudentGeneratePasswordScreen,
  StudentInventoryScreen,
  StudentLedgerScreen,
  StudentLedgerInstallmentsScreen,
  StudentMessagesScreen,
  StudentPerformanceScreen,
  StudentReceiptScreen,
  StudentRemarksScreen,
  StudentReportCardScreen,
  StudentSendReportEmailScreen,
  StudentLandingListScreen,
  StudentLandingEntryScreen,
  AdmissionFormSettingListScreen,
  AdmissionFormSettingEntryScreen,
  InstallmentsListScreen,
  BirthdaysListScreen,
  BirthdaysEntryScreen,
  BiometricRecordsListScreen,
  BiometricRecordsEntryScreen,
  UploadExcelListScreen,
  UploadExcelEntryScreen,
  AdmissionAnalyticsListScreen,
  AdmissionAnalyticsEntryScreen,
  FeesListScreen,
  FeesEntryScreen,
  FeesSendSmsScreen,
  IssueItemListScreen,
  IssueItemEntryScreen,
} from './features/admission'

// Education Suite
import LectureListScreen from './features/lectures/lectures-list/LectureListScreen'
import LectureEntryScreen from './features/lectures/lectures-list/LectureEntryScreen'
import TimetableListScreen from './features/lectures/timetable/TimetableListScreen'
import TimetableEntryScreen from './features/lectures/timetable/TimetableEntryScreen'
import HoursScreen from './features/lectures/hours/HoursScreen'
import SyllabusListScreen from './features/lectures/syllabus/SyllabusListScreen'
import SyllabusEntryScreen from './features/lectures/syllabus/SyllabusEntryScreen'
import UploadScreen from './features/lectures/upload/UploadScreen'
import SettingsScreen from './features/lectures/settings/SettingsScreen'
import DailyAttendanceScreen from './features/attendance/daily/DailyAttendanceScreen'
import MonthlyAttendanceScreen from './features/attendance/monthly/MonthlyAttendanceScreen'
import BiometricRecordsScreen from './features/attendance/biometric/BiometricRecordsScreen'
import UploadSwipesScreen from './features/attendance/upload-swipes/UploadSwipesScreen'
import AttendanceSettingsScreen from './features/attendance/settings/AttendanceSettingsScreen'
// Tests Suite
import TestListScreen from './features/tests/tests/TestListScreen'
import TestEntryScreen from './features/tests/tests/TestEntryScreen'
import TestMarks from './features/tests/tests/TestMarks'
import QuestionPaper from './features/tests/tests/QuestionPaper'
import TestSolution from './features/tests/tests/TestSolution'
import TestStatistics from './features/tests/tests/TestStatistics'
import TestGroupListScreen from './features/tests/testgroups/TestGroupListScreen'
import TestGroupEntryScreen from './features/tests/testgroups/TestGroupEntryScreen'
import TestSettingsScreen from './features/tests/test-settings/TestSettingsScreen'
import ReportCardSettingsScreen from './features/tests/report-card-settings/ReportCardSettingsScreen'
import RemarksListScreen from './features/remarks/RemarksListScreen'
import RemarksEntryScreen from './features/remarks/RemarksEntryScreen'
import MobileAppScreen from './features/cloud/mobile-app/MobileAppScreen'
import NoticeListScreen from './features/cloud/notice/NoticeListScreen'
import NoticeEntryScreen from './features/cloud/notice/NoticeEntryScreen'
import EventsListScreen from './features/cloud/events/EventsListScreen'
import EventsEntryScreen from './features/cloud/events/EventsEntryScreen'
import GalleryScreen from './features/cloud/gallery/GalleryScreen'
import LeadFormScreen from './features/cloud/lead/LeadFormScreen'
import FeedbackListScreen from './features/cloud/feedback/FeedbackListScreen'
import FeedbackReplyScreen from './features/cloud/feedback/FeedbackReplyScreen'
import StudentLogsScreen from './features/cloud/student-logs/StudentLogsScreen'

// Productivity (Bruno Suite)
import ProductivityDashboardScreen from './features/productivity/dashboard/ProductivityDashboardScreen'
import ProductivitySessionsListScreen from './features/productivity/sessions/ProductivitySessionsListScreen'
import RedDomainsScreen from './features/productivity/red-domains/RedDomainsScreen'

// Project Management (Nerve Suite)
import ProjectInventoryListScreen from './features/project-management/project/ProjectInventoryListScreen'
import ProjectConfigurationEntryScreen from './features/project-management/project/ProjectConfigurationEntryScreen'
import ProjectDetailScreen from './features/project-management/project/ProjectDetailScreen'
import ProjectBulkUploadScreen from './features/project-management/project/ProjectBulkUploadScreen'
import TaskLedgerScreen from './features/project-management/task/TaskLedgerScreen'
import TaskConfigurationScreen from './features/project-management/task/TaskConfigurationScreen'
import TaskBulkUploadScreen from './features/project-management/task/TaskBulkUploadScreen'
import JobInventoryListScreen from './features/project-management/job/JobInventoryListScreen'
import JobConfigurationScreen from './features/project-management/job/JobConfigurationScreen'
import TimesheetListScreen from './features/project-management/timesheet/TimesheetListScreen'
import TimesheetEntryScreen from './features/project-management/timesheet/TimesheetEntryScreen'
import TicketInflowRegistryScreen from './features/project-management/ticketsync/TicketInflowRegistryScreen'
import ResourceAllocationBoardScreen from './features/allocation/ResourceAllocationBoardScreen'

// Approvals
import CompanyApprovalScreen from './features/approval/company/CompanyApprovalScreen'
import MasterApprovalScreen from './features/approval/masters/MasterApprovalScreen'
import TimesheetApprovalScreen from './features/approval/timesheet/TimesheetApprovalScreen'
import LeaveApprovalScreen from './features/approval/leave/LeaveApprovalScreen'
import ProductivityAdjustmentApprovalScreen from './features/approval/productivity/ProductivityAdjustmentApprovalScreen'
import ProductivityAdjustmentApprovalDetailScreen from './features/approval/productivity/ProductivityAdjustmentApprovalDetailScreen'

// Support
import SupportTicketManagementScreen from './features/support-ticket/SupportTicketManagementScreen'
import SupportTicketEntryScreen from './features/support-ticket/SupportTicketEntryScreen'

// Analytics, Communication, Telephony, Reports, Notifications, Audit Logs
import AnalyticsScreen from './features/analytics/AnalyticsScreen'
import CommunicationScreen from './features/communication/CommunicationScreen'
import CallCenterScreen from './features/telephony/call-center/CallCenterScreen'
import CallLogsScreen from './features/telephony/call-logs/CallLogsScreen'
import TelephonySettingsScreen from './features/telephony/settings/TelephonySettingsScreen'
import ReportsScreen from './features/reports/ReportsScreen'
import ReportSchedulesListScreen from './features/reports/ReportSchedulesListScreen'
import ReportScheduleEntryScreen from './features/reports/ReportScheduleEntryScreen'
import NotificationScreen from './features/notifications/NotificationScreen'
import AuditLogScreen from './features/audit-logs/AuditLogScreen'
import EmailNotificationSettingsScreen from './features/email-settings/EmailNotificationSettingsScreen'

// Employee Self-Service (ESS)
import EmployeeLoginScreen from './features/self-service/auth/EmployeeLoginScreen'
import EmployeeDashboardScreen from './features/self-service/dashboard/EmployeeDashboardScreen'
import MyProfileScreen from './features/self-service/my-profile/MyProfileScreen'
import CompanyPolicyScreen from './features/self-service/company-policy/CompanyPolicyScreen'
import ESSAttendanceHistoryListScreen from './features/self-service/attendance/attendance-history/AttendanceHistoryListScreen'
import AttendanceRegularizationListScreen from './features/self-service/attendance/attendance-regularization/AttendanceRegularizationListScreen'
import AttendanceRegularizationEntryScreen from './features/self-service/attendance/attendance-regularization/AttendanceRegularizationEntryScreen'
import ESSHolidayListScreen from './features/self-service/holiday/HolidayListScreen'
import LeaveBalanceListScreen from './features/self-service/leave-balance/LeaveBalanceListScreen'
import LeaveApplyListScreen from './features/self-service/leave-apply/LeaveApplyListScreen'
import LeaveApplyEntryScreen from './features/self-service/leave-apply/LeaveApplyEntryScreen'
import LoanApplicationListScreen from './features/self-service/loan-application/LoanApplicationListScreen'
import LoanApplicationEntryScreen from './features/self-service/loan-application/LoanApplicationEntryScreen'
import PayslipListScreen from './features/self-service/payslip/PayslipListScreen'
import EmpIncentivesListScreen from './features/self-service/emp-incentives/EmpIncentivesListScreen'
import MyTeamListScreen from './features/self-service/my-team/MyTeamListScreen'
import MySubordinatesListScreen from './features/self-service/my-subordinates/MySubordinatesListScreen'
import SearchEmployeeListScreen from './features/self-service/search-employee/SearchEmployeeListScreen'

import './App.css'

function CommunicationRouteWrapper() {
  const { module = 'hub' } = useParams()
  return (
    <div className="h-full min-h-0 communication-route-shell">
      <CommunicationScreen tab={module} />
    </div>
  )
}

function LegacyRedirect({ to }) {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)
  const targetPrefix = to.replace(/^\//, '')
  segments[0] = targetPrefix
  const targetPath = '/' + segments.join('/') + location.search + location.hash
  return <Navigate to={targetPath} replace />
}

function SectionIndexRedirect({ menuId }) {
  const { menus } = useAuth()
  const targetPath = useMemo(() => {
    const menu = menus.find((m) =>
      m.id === menuId ||
      (menuId === 'nerve' && (m.id === 'project-management' || m.id === 'nerve')) ||
      (menuId === 'project-management' && (m.id === 'nerve' || m.id === 'project-management')) ||
      (menuId === 'bruno' && (m.id === 'productivity' || m.id === 'bruno')) ||
      (menuId === 'productivity' && (m.id === 'bruno' || m.id === 'productivity')) ||
      (menuId === 'bridge' && (m.id === 'communication' || m.id === 'bridge')) ||
      (menuId === 'communication' && (m.id === 'bridge' || m.id === 'communication')) ||
      (menuId === 'loop' && (m.id === 'telephony' || m.id === 'loop')) ||
      (menuId === 'telephony' && (m.id === 'loop' || m.id === 'telephony'))
    )
    return menu?.defaultPath || '/'
  }, [menus, menuId])

  return <Navigate to={targetPath} replace />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* AppNotificationBusProvider wraps everything — TelephonyContext (inside BrowserRouter)
          can call useNotificationBus() because the bus is above the router */}
      <AppNotificationBusProvider>
        <AppTooltipProvider>
          <Toaster
            position="top-center"
            offset="90px"
            style={{ '--offset': '90px', zIndex: 40 }}
            toastOptions={{
              unstyled: true,
              classNames: {
                toast: 'font-body rounded-xl border bg-surface-container-lowest text-on-surface shadow-[0_14px_32px_rgba(15,23,42,0.15)] p-4 flex gap-3 items-center min-w-[320px] max-w-[450px]',
                title: 'text-sm font-semibold font-display text-on-surface',
                description: 'text-xs text-on-surface-variant mt-0.5',
                error: 'border-error/30 bg-error-container text-error',
                success: 'border-green-600/30 bg-green-50 text-green-700',
              }
            }}
          />
          <AppConfirmModalRoot />
          {/* GlobalCallPopup is OUTSIDE BrowserRouter so it survives all route changes.
              It communicates with TelephonyContext via window custom events. */}
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            {/* GlobalCallPopup needs useNavigate so must be inside BrowserRouter */}
            <GlobalCallPopup />
            <Routes>
              {/* ── Public Auth Routes ── */}
              <Route path="/auth/callback" element={<AuthCallbackScreen />} />
              <Route
                path="/login"
                element={
                  <PublicOnlyRoute>
                    <LoginScreen />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/self-service/login"
                element={<EmployeeLoginScreen />}
              />
              <Route
                path="/auth/otp-verification"
                element={
                  <PublicOnlyRoute>
                    <OTPVerificationScreen />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/auth/password-reset"
                element={
                  <PublicOnlyRoute>
                    <PasswordResetScreen />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/system-check"
                element={<SystemVerificationScreen />}
              />

              {/* ── Detached Call Popup Window (frameless mini desktop card) ── */}
              <Route path="/loop/call-popup" element={<DetachedCallPopupWindow />} />
              <Route path="/telephony/call-popup" element={<DetachedCallPopupWindow />} />

              {/* ── Protected Shell Routes ── */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />

                {/* ── Dashboard ── */}
                <Route path="dashboard">
                  <Route index element={<DashboardScreen />} />
                  <Route path=":tab" element={<DashboardScreen />} />
                </Route>

                {/* ── Masters ── */}
                <Route path="masters">
                  <Route index element={<SectionIndexRedirect menuId="masters" />} />

                  {/* Company */}
                  <Route path="company/list" element={<PermissionGuard privilege="CompanyView" routeLabel="Company Master"><CompanyMasterListScreen /></PermissionGuard>} />
                  <Route path="company/entry" element={<PermissionGuard anyOf={['CompanyAdd', 'CompanyEdit']} routeLabel="Company Master"><CompanyMasterEntryScreen /></PermissionGuard>} />

                  {/* Branch */}
                  <Route path="branch/list" element={<PermissionGuard privilege="BranchView" routeLabel="Branch Master"><BranchMasterListScreen /></PermissionGuard>} />
                  <Route path="branch/entry" element={<PermissionGuard anyOf={['BranchAdd', 'BranchEdit']} routeLabel="Branch Master"><BranchMasterEntryScreen /></PermissionGuard>} />

                  {/* Department */}
                  <Route path="department/list" element={<PermissionGuard privilege="DepartmentView" routeLabel="Department Master"><DepartmentMasterListScreen /></PermissionGuard>} />
                  <Route path="department/entry" element={<PermissionGuard anyOf={['DepartmentAdd', 'DepartmentEdit']} routeLabel="Department Master"><DepartmentMasterEntryScreen /></PermissionGuard>} />

                  {/* Roles */}
                  <Route path="roles/list" element={<PermissionGuard privilege="RoleView" routeLabel="Role Matrix"><RoleRightsListScreen /></PermissionGuard>} />
                  <Route path="roles/entry" element={<PermissionGuard anyOf={['RoleAdd', 'RoleEdit']} routeLabel="Role Matrix"><RoleConfigurationScreen /></PermissionGuard>} />

                  {/* Users */}
                  <Route path="users/list" element={<PermissionGuard privilege="UserView" routeLabel="User Directory"><UserDirectoryScreen /></PermissionGuard>} />
                  <Route path="users/entry" element={<PermissionGuard anyOf={['UserAdd', 'UserEdit']} routeLabel="User Directory"><UserProfileManagementScreen /></PermissionGuard>} />

                  {/* Subscriptions & Trials */}
                  <Route path="subscriptions" element={<PermissionGuard privilege="CompanyView" routeLabel="Subscriptions"><SubscriptionSettingsScreen /></PermissionGuard>} />

                  {/* Task Master */}
                  <Route path="task-master/list" element={<PermissionGuard privilege="TaskMasterView" routeLabel="Task Master"><TaskMasterListScreen /></PermissionGuard>} />
                  <Route path="task-master/entry" element={<PermissionGuard anyOf={['TaskMasterAdd', 'TaskMasterEdit']} routeLabel="Task Master"><TaskMasterEntryScreen /></PermissionGuard>} />

                  {/* Job Master */}
                  <Route path="job-master/list" element={<PermissionGuard privilege="JobMasterView" routeLabel="Job Master"><JobMasterListScreen /></PermissionGuard>} />
                  <Route path="job-master/entry" element={<PermissionGuard anyOf={['JobMasterAdd', 'JobMasterEdit']} routeLabel="Job Master"><JobMasterEntryScreen /></PermissionGuard>} />

                  {/* Currency */}
                  <Route path="currency/list" element={<PermissionGuard privilege="CompanyView" routeLabel="Currency Master"><CurrencyMasterScreen /></PermissionGuard>} />
                  <Route path="currency/entry" element={<PermissionGuard anyOf={['CompanyAdd', 'CompanyEdit']} routeLabel="Currency Master"><CurrencyMasterEntryScreen /></PermissionGuard>} />

                  {/* A/C Heads Submodules */}
                  <Route path="acheads/heads/list" element={<AcHeadListScreen />} />
                  <Route path="acheads/heads/entry" element={<AcHeadEntryScreen />} />
                  <Route path="acheads/vendors/list" element={<VendorListScreen />} />
                  <Route path="acheads/vendors/entry" element={<VendorEntryScreen />} />
                  <Route path="acheads/banks/list" element={<BankListScreen />} />
                  <Route path="acheads/banks/entry" element={<BankEntryScreen />} />
                  <Route path="acheads/employees/list" element={<EmployeeListScreen />} />
                  <Route path="acheads/employees/entry" element={<EmployeeEntryScreen />} />
                  <Route path="acheads/employees/entry/:id" element={<EmployeeEntryScreen />} />
                  <Route path="acheads/employees/:id" element={<EmployeeEntryScreen />} />
                  <Route path="acheads/customers/list" element={<CustomerListScreen />} />
                  <Route path="acheads/customers/entry" element={<CustomerEntryScreen />} />
                  <Route path="acheads/cust-cash/list" element={<CustCashListScreen />} />
                  <Route path="acheads/cust-cash/entry" element={<CustCashEntryScreen />} />

                  {/* Academic & Education Masters */}
                  <Route path="courses/list" element={<CoursesListScreen />} />
                  <Route path="courses/entry" element={<CoursesEntryScreen />} />
                  <Route path="batches/list" element={<BatchesListScreen />} />
                  <Route path="batches/entry" element={<BatchesEntryScreen />} />
                  <Route path="subjects/list" element={<SubjectsListScreen />} />
                  <Route path="subjects/entry" element={<SubjectsEntryScreen />} />
                  <Route path="schemes/list" element={<SchemesListScreen />} />
                  <Route path="schemes/entry" element={<SchemesEntryScreen />} />
                  <Route path="remarks/list" element={<RemarksListScreen />} />
                  <Route path="remarks/entry" element={<RemarksEntryScreen />} />
                  <Route path="classroom/list" element={<ClassroomListScreen />} />
                  <Route path="classroom/entry" element={<ClassroomEntryScreen />} />
                  <Route path="boards/list" element={<BoardsListScreen />} />
                  <Route path="boards/entry" element={<BoardsEntryScreen />} />
                  <Route path="medium/list" element={<MediumListScreen />} />
                  <Route path="medium/entry" element={<MediumEntryScreen />} />
                  <Route path="school-college/list" element={<SchoolsListScreen />} />
                  <Route path="school-college/entry" element={<SchoolsEntryScreen />} />
                  <Route path="schools/list" element={<SchoolsListScreen />} />
                  <Route path="schools/entry" element={<SchoolsEntryScreen />} />
                  <Route path="caste" element={<CasteListScreen />} />
                  <Route path="caste/list" element={<CasteListScreen />} />
                  <Route path="caste/entry" element={<CasteEntryScreen />} />
                  <Route path="sub-caste" element={<SubCasteListScreen />} />
                  <Route path="sub-caste/list" element={<SubCasteListScreen />} />
                  <Route path="sub-caste/entry" element={<SubCasteEntryScreen />} />
                  {/* Occupation (Singular & Plural Aliases) */}
                  <Route path="occupation/list" element={<OccupationListScreen />} />
                  <Route path="occupation/entry" element={<OccupationEntryScreen />} />
                  <Route path="occupations/list" element={<Navigate to="/masters/occupation/list" replace />} />
                  <Route path="occupations/entry" element={<Navigate to="/masters/occupation/entry" replace />} />

                  {/* Relation (Singular & Plural Aliases) */}
                  <Route path="relation/list" element={<RelationListScreen />} />
                  <Route path="relation/entry" element={<RelationEntryScreen />} />
                  <Route path="relation/entry/:id" element={<RelationEntryScreen />} />
                  <Route path="relations/list" element={<Navigate to="/masters/relation/list" replace />} />
                  <Route path="relations/entry" element={<Navigate to="/masters/relation/entry" replace />} />
                  <Route path="subject-fees/list" element={<SubjectFeesListScreen />} />
                  <Route path="subject-fees/entry" element={<SubjectFeesEntryScreen />} />
                  <Route path="subject-fees/entry/:id" element={<SubjectFeesEntryScreen />} />
                  <Route path="place/list" element={<PlaceListScreen />} />
                  <Route path="place/entry" element={<PlaceEntryScreen />} />
                  <Route path="place/entry/:id" element={<PlaceEntryScreen />} />
                  <Route path="qualification/list" element={<QualificationListScreen />} />
                  <Route path="qualification/entry" element={<QualificationEntryScreen />} />
                  <Route path="qualification/entry/:id" element={<QualificationEntryScreen />} />
                  <Route path="sources/list" element={<SourcesListScreen />} />
                  <Route path="sources/entry" element={<SourcesEntryScreen />} />
                  <Route path="sources/entry/:id" element={<SourcesEntryScreen />} />
                  <Route path="employee-document-master/list" element={<EmployeeDocumentMasterListScreen />} />
                  <Route path="employee-document-master/entry" element={<EmployeeDocumentMasterEntryScreen />} />
                  <Route path="employee-document-master/entry/:id" element={<EmployeeDocumentMasterEntryScreen />} />
                  <Route path="remarks-category/list" element={<RemarksCategoryListScreen />} />
                  <Route path="remarks-category/entry" element={<RemarksCategoryEntryScreen />} />
                  <Route path="remarks-category/entry/:id" element={<RemarksCategoryEntryScreen />} />
                  <Route path="contact-category/list" element={<ContactCategoryListScreen />} />
                  <Route path="contact-category/entry" element={<ContactCategoryEntryScreen />} />
                  <Route path="contact-category/entry/:id" element={<ContactCategoryEntryScreen />} />
                  <Route path="reason-type/list" element={<ReasonTypeListScreen />} />
                  <Route path="reason-type/entry" element={<ReasonTypeEntryScreen />} />
                  <Route path="reason-type/entry/:id" element={<ReasonTypeEntryScreen />} />
                  <Route path="inventory-group/list" element={<InventoryGroupListScreen />} />
                  <Route path="inventory-group/entry" element={<InventoryGroupEntryScreen />} />
                  <Route path="inventory-group/entry/:id" element={<InventoryGroupEntryScreen />} />
                  <Route path="brand/list" element={<BrandListScreen />} />
                  <Route path="brand/entry" element={<BrandEntryScreen />} />
                  <Route path="payment-mode/list" element={<PaymentModeListScreen />} />
                  <Route path="payment-mode/entry" element={<PaymentModeEntryScreen />} />
                  <Route path="inventory/list" element={<InventoryListScreen />} />
                  <Route path="inventory/entry" element={<InventoryEntryScreen />} />

                  {/* Employee Group Master */}
                  <Route path="employee-group" element={<EmployeeGroupListScreen />} />
                  <Route path="employee-group/list" element={<EmployeeGroupListScreen />} />
                  <Route path="employee-group/entry" element={<EmployeeGroupEntryScreen />} />
                  <Route path="employee-group/entry/:id" element={<EmployeeGroupEntryScreen />} />

                  {/* Holiday Master */}
                  <Route path="holiday-master" element={<HolidayMasterListScreen />} />
                  <Route path="holiday-master/list" element={<HolidayMasterListScreen />} />
                  <Route path="holiday-master/entry" element={<HolidayMasterEntryScreen />} />
                  <Route path="holiday-master/entry/:id" element={<HolidayMasterEntryScreen />} />

                  {/* Financial Operations & Generic Masters Fallback */}
                  <Route path=":module/list" element={<SharedMasterPage />} />
                  <Route path=":module/entry" element={<SharedMasterEntryScreen />} />
                </Route>

                {/* ── Admission Module ── */}
                <Route path="admission">
                  <Route index element={<Navigate to="students/list" replace />} />

                  {/* 1. Students */}
                  <Route path="students/list" element={<StudentsListScreen />} />
                  <Route path="students/entry" element={<StudentsEntryScreen />} />
                  <Route path="students/entry/:id" element={<StudentsEntryScreen />} />
                  <Route path="students/attendance" element={<StudentAttendanceScreen />} />
                  <Route path="students/biometric" element={<StudentBiometricScreen />} />
                  <Route path="students/document" element={<StudentDocumentScreen />} />
                  <Route path="students/password" element={<StudentGeneratePasswordScreen />} />
                  <Route path="students/inventory" element={<StudentInventoryScreen />} />
                  <Route path="students/ledger" element={<StudentLedgerScreen />} />
                  <Route path="students/ledger-installments" element={<StudentLedgerInstallmentsScreen />} />
                  <Route path="students/messages" element={<StudentMessagesScreen />} />
                  <Route path="students/performance" element={<StudentPerformanceScreen />} />
                  <Route path="students/receipt" element={<StudentReceiptScreen />} />
                  <Route path="students/remarks" element={<StudentRemarksScreen />} />
                  <Route path="students/report-card" element={<StudentReportCardScreen />} />
                  <Route path="students/send-report" element={<StudentSendReportEmailScreen />} />

                  {/* 2. Student Landing Page */}
                  <Route path="student-landing/list" element={<StudentLandingListScreen />} />
                  <Route path="student-landing/entry" element={<StudentLandingEntryScreen />} />
                  <Route path="student-landing/entry/:id" element={<StudentLandingEntryScreen />} />

                  {/* 3. Admission Form Setting */}
                  <Route path="admission-form-setting/list" element={<AdmissionFormSettingListScreen />} />
                  <Route path="admission-form-setting/entry" element={<AdmissionFormSettingEntryScreen />} />
                  <Route path="admission-form-setting/entry/:id" element={<AdmissionFormSettingEntryScreen />} />

                  {/* 4. Installments (Report Only - 100% Legacy Parity) */}
                  <Route path="installments/list" element={<InstallmentsListScreen />} />
                  <Route path="installments/entry" element={<Navigate to="/admission/installments/list" replace />} />
                  <Route path="installments/entry/:id" element={<Navigate to="/admission/installments/list" replace />} />

                  {/* 5. Birthdays */}
                  <Route path="birthdays/list" element={<BirthdaysListScreen />} />
                  <Route path="birthdays/entry" element={<BirthdaysEntryScreen />} />
                  <Route path="birthdays/entry/:id" element={<BirthdaysEntryScreen />} />

                  {/* 6. Biometric Records */}
                  <Route path="biometric-records/list" element={<BiometricRecordsListScreen />} />
                  <Route path="biometric-records/entry" element={<BiometricRecordsEntryScreen />} />
                  <Route path="biometric-records/entry/:id" element={<BiometricRecordsEntryScreen />} />

                  {/* 7. Upload Excel */}
                  <Route path="upload-excel/list" element={<UploadExcelListScreen />} />
                  <Route path="upload-excel/entry" element={<UploadExcelEntryScreen />} />

                  {/* 8. Analytics */}
                  <Route path="analytics/list" element={<AdmissionAnalyticsListScreen />} />
                  <Route path="analytics/entry" element={<AdmissionAnalyticsEntryScreen />} />

                  {/* 9. Fees */}
                  <Route path="fees/list" element={<FeesListScreen />} />
                  <Route path="fees/entry" element={<FeesEntryScreen />} />
                  <Route path="fees/entry/:id" element={<FeesEntryScreen />} />
                  <Route path="fees/send-sms" element={<FeesSendSmsScreen />} />

                  {/* 10. Issue Item */}
                  <Route path="issue-item/list" element={<IssueItemListScreen />} />
                  <Route path="issue-item/entry" element={<IssueItemEntryScreen />} />
                  <Route path="issue-item/entry/:id" element={<IssueItemEntryScreen />} />

                  {/* Legacy Redirect: Issue Item Category is Inventory Group Master */}
                  <Route path="issue-item-category/*" element={<Navigate to="/masters/inventory-group/list" replace />} />
                </Route>

                {/* ── Lectures ── */}
                <Route path="lectures">
                  <Route index element={<Navigate to="list" replace />} />
                  <Route path="list" element={<LectureListScreen />} />
                  <Route path="entry" element={<LectureEntryScreen />} />
                  <Route path="entry/:id" element={<LectureEntryScreen />} />
                  <Route path="timetable" element={<TimetableListScreen />} />
                  <Route path="timetable/entry" element={<TimetableEntryScreen />} />
                  <Route path="timetable/entry/:id" element={<TimetableEntryScreen />} />
                  <Route path="hours" element={<HoursScreen />} />
                  <Route path="syllabus" element={<SyllabusListScreen />} />
                  <Route path="syllabus/entry" element={<SyllabusEntryScreen />} />
                  <Route path="syllabus/entry/:id" element={<SyllabusEntryScreen />} />
                  <Route path="upload" element={<UploadScreen />} />
                  <Route path="settings" element={<SettingsScreen />} />
                </Route>

                {/* ── Attendance ── */}
                <Route path="attendance">
                  <Route index element={<Navigate to="daily" replace />} />
                  <Route path="daily" element={<DailyAttendanceScreen />} />
                  <Route path="monthly" element={<MonthlyAttendanceScreen />} />
                  <Route path="biometric" element={<BiometricRecordsScreen />} />
                  <Route path="upload-swipes" element={<UploadSwipesScreen />} />
                  <Route path="settings" element={<AttendanceSettingsScreen />} />
                </Route>

                {/* ── Tests Suite ── */}
                <Route path="education/tests">
                  <Route index element={<TestListScreen />} />
                  <Route path="list" element={<Navigate to="/education/tests" replace />} />
                  <Route path="entry" element={<TestEntryScreen />} />
                  <Route path="marks" element={<TestMarks />} />
                  <Route path="question-paper" element={<QuestionPaper />} />
                  <Route path="solution" element={<TestSolution />} />
                  <Route path="statistics" element={<TestStatistics />} />
                  <Route path="groups" element={<TestGroupListScreen />} />
                  <Route path="groups/entry" element={<TestGroupEntryScreen />} />
                  <Route path="settings" element={<TestSettingsScreen />} />
                  <Route path="report-card-settings" element={<ReportCardSettingsScreen />} />
                </Route>
                <Route path="tests" element={<Navigate to="/education/tests" replace />} />
                <Route path="tests/*" element={<LegacyRedirect to="/education/tests" />} />
                <Route path="education/test-groups" element={<Navigate to="/education/tests/groups" replace />} />
                <Route path="education/test-groups/*" element={<LegacyRedirect to="/education/tests/groups" />} />
                <Route path="education/test-settings" element={<Navigate to="/education/tests/settings" replace />} />
                <Route path="education/report-card-settings" element={<Navigate to="/education/tests/report-card-settings" replace />} />

                {/* ── Remarks ── */}
                <Route path="remarks">
                  <Route index element={<Navigate to="list" replace />} />
                  <Route path="list" element={<RemarksListScreen />} />
                  <Route path="entry" element={<RemarksEntryScreen />} />
                  <Route path="edit/:id" element={<RemarksEntryScreen />} />
                </Route>

                {/* ── Cloud ── */}
                <Route path="cloud">
                  <Route index element={<Navigate to="mobile-app" replace />} />
                  <Route path="mobile-app" element={<MobileAppScreen />} />
                  <Route path="notice">
                    <Route index element={<Navigate to="list" replace />} />
                    <Route path="list" element={<NoticeListScreen />} />
                    <Route path="entry" element={<NoticeEntryScreen />} />
                    <Route path="edit/:id" element={<NoticeEntryScreen />} />
                  </Route>
                  <Route path="events">
                    <Route index element={<Navigate to="list" replace />} />
                    <Route path="list" element={<EventsListScreen />} />
                    <Route path="entry" element={<EventsEntryScreen />} />
                    <Route path="edit/:id" element={<EventsEntryScreen />} />
                  </Route>
                  <Route path="gallery" element={<GalleryScreen />} />
                  <Route path="lead" element={<LeadFormScreen />} />
                  <Route path="feedback">
                    <Route index element={<Navigate to="list" replace />} />
                    <Route path="list" element={<FeedbackListScreen />} />
                    <Route path="reply" element={<Navigate to="/cloud/feedback/list" replace />} />
                    <Route path="reply/:id" element={<FeedbackReplyScreen />} />
                  </Route>
                  <Route path="student-logs" element={<StudentLogsScreen />} />
                </Route>

                {/* ── Payroll ── */}
                <Route path="payroll">
                  <Route index element={<Navigate to="/payroll/employee" replace />} />
                  <Route path="employee" element={<EmployeeListScreen />} />
                  <Route path="employee/list" element={<EmployeeListScreen />} />
                  <Route path="employee/entry" element={<EmployeeEntryScreen />} />
                  <Route path="employee/entry/:id" element={<EmployeeEntryScreen />} />
                  <Route path="employee/:id" element={<EmployeeEntryScreen />} />
                  <Route path="employees" element={<Navigate to="/payroll/employee" replace />} />
                  <Route path="employee-group" element={<EmployeeGroupListScreen />} />
                  <Route path="employee-group/list" element={<EmployeeGroupListScreen />} />
                  <Route path="employee-group/entry" element={<EmployeeGroupEntryScreen />} />
                  <Route path="employee-group/entry/:id" element={<EmployeeGroupEntryScreen />} />
                  <Route path="holiday-master" element={<HolidayMasterListScreen />} />
                  <Route path="holiday-master/list" element={<HolidayMasterListScreen />} />
                  <Route path="holiday-master/entry" element={<HolidayMasterEntryScreen />} />
                  <Route path="holiday-master/entry/:id" element={<HolidayMasterEntryScreen />} />
                  <Route path="payheads" element={<PayHeadsListScreen />} />
                  <Route path="payheads/list" element={<PayHeadsListScreen />} />
                  <Route path="payheads/entry" element={<PayHeadsEntryScreen />} />
                  <Route path="payheads/entry/:id" element={<PayHeadsEntryScreen />} />
                  <Route path="biometrics" element={<BiometricsScreen />} />
                  <Route path="manual-punch" element={<ManualPunchListScreen />} />
                  <Route path="manual-punch/list" element={<ManualPunchListScreen />} />
                  <Route path="manual-punch/entry" element={<ManualPunchEntryScreen />} />
                  <Route path="manual-punch/upload" element={<ManualPunchBulkUploadScreen />} />
                  <Route path="remote-attandance" element={<RemoteAttendanceListScreen />} />
                  <Route path="remote-attandance/list" element={<RemoteAttendanceListScreen />} />
                  <Route path="remote-attandance/entry" element={<RemoteAttendanceEntryScreen />} />
                  <Route path="remote-attandance/upload" element={<RemoteAttendanceBulkUploadScreen />} />
                  <Route path="remote-attendance" element={<RemoteAttendanceListScreen />} />
                  <Route path="remote-attendance/list" element={<RemoteAttendanceListScreen />} />
                  <Route path="remote-attendance/entry" element={<RemoteAttendanceEntryScreen />} />
                  <Route path="remote-attendance/upload" element={<RemoteAttendanceBulkUploadScreen />} />
                  <Route path="attendance-history" element={<AttendanceHistoryListScreen />} />
                  <Route path="attendance-history/list" element={<AttendanceHistoryListScreen />} />
                  <Route path="shift-master" element={<ShiftMasterListScreen />} />
                  <Route path="shift-master/list" element={<ShiftMasterListScreen />} />
                  <Route path="shift-master/entry" element={<ShiftMasterEntryScreen />} />
                  <Route path="leave-master" element={<LeaveMasterListScreen />} />
                  <Route path="leave-master/list" element={<LeaveMasterListScreen />} />
                  <Route path="leave-master/entry" element={<LeaveMasterEntryScreen />} />
                  <Route path="leave-master/entry/:id" element={<LeaveMasterEntryScreen />} />
                  <Route path="leave-rules" element={<LeaveRulesListScreen />} />
                  <Route path="leave-rules/list" element={<LeaveRulesListScreen />} />
                  <Route path="leave-rules/entry" element={<LeaveRulesEntryScreen />} />
                  <Route path="leave-rules/entry/:id" element={<LeaveRulesEntryScreen />} />
                  <Route path="leave-rules/details" element={<LeaveRulesDetailScreen />} />
                  <Route path="leave-rules/details/:id" element={<LeaveRulesDetailScreen />} />
                  <Route path="leave-application" element={<LeaveApplicationListScreen />} />
                  <Route path="leave-application/list" element={<LeaveApplicationListScreen />} />
                  <Route path="leave-application/entry" element={<LeaveApplicationEntryScreen />} />
                  <Route path="leave-application/entry/:id" element={<LeaveApplicationEntryScreen />} />
                  <Route path="incentives" element={<IncentivesListScreen />} />
                  <Route path="incentives/list" element={<IncentivesListScreen />} />
                  <Route path="incentives/entry" element={<IncentivesEntryScreen />} />
                  <Route path="incentives/entry/:id" element={<IncentivesEntryScreen />} />
                  <Route path="deductions" element={<DeductionsListScreen />} />
                  <Route path="deductions/list" element={<DeductionsListScreen />} />
                  <Route path="deductions/entry" element={<DeductionsEntryScreen />} />
                  <Route path="deductions/entry/:id" element={<DeductionsEntryScreen />} />
                  <Route path="attendance-rules" element={<AttendanceRulesListScreen />} />
                  <Route path="attendance-rules/list" element={<AttendanceRulesListScreen />} />
                  <Route path="attendance-rules/entry" element={<AttendanceRulesEntryScreen />} />
                  <Route path="daily-process" element={<DailyProcessListScreen />} />
                  <Route path="daily-process/list" element={<DailyProcessListScreen />} />
                  <Route path="daily-process/run" element={<DailyProcessRunScreen />} />
                  <Route path="monthly-process" element={<MonthlyProcessListScreen />} />
                  <Route path="monthly-process/list" element={<MonthlyProcessListScreen />} />
                  <Route path="monthly-process/run" element={<MonthlyProcessRunScreen />} />
                  <Route path="loans" element={<LoansScreen />} />
                  <Route path="loans/entry" element={<LoansEntryScreen />} />
                  <Route path="loans/apply" element={<LoansEntryScreen />} />
                  <Route path="salary-process" element={<SalaryProcessScreen />} />
                  <Route path="salary-process/process" element={<SalaryProcessEntryScreen />} />
                  <Route path="salary-process/entry" element={<SalaryProcessEntryScreen />} />
                  <Route path="settings" element={<PayrollSettingsScreen />} />
                </Route>

                {/* ── Bruno Suite ── */}
                <Route path="bruno">
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<ProductivityDashboardScreen />} />
                  <Route path="sessions" element={<ProductivitySessionsListScreen />} />
                  <Route path="red-domains" element={<RedDomainsScreen />} />
                </Route>
                <Route path="productivity" element={<Navigate to="/bruno/dashboard" replace />} />
                <Route path="productivity/*" element={<LegacyRedirect to="/bruno" />} />

                {/* ── Nerve Suite ── */}
                <Route path="nerve">
                  <Route index element={<SectionIndexRedirect menuId="nerve" />} />
                  <Route path="project/list" element={<PermissionGuard privilege="ProjectView" module="nerve" routeLabel="Project Inventory"><ProjectInventoryListScreen /></PermissionGuard>} />
                  <Route path="project/entry" element={<PermissionGuard anyOf={['ProjectAdd', 'ProjectEdit']} module="nerve" routeLabel="Project Configuration"><ProjectConfigurationEntryScreen /></PermissionGuard>} />
                  <Route path="project/detail" element={<PermissionGuard privilege="ProjectView" module="nerve" routeLabel="Project Detail"><ProjectDetailScreen /></PermissionGuard>} />
                  <Route path="project/bulk-upload" element={<PermissionGuard privilege="ProjectAdd" module="nerve" routeLabel="Project Bulk Upload"><ProjectBulkUploadScreen /></PermissionGuard>} />

                  <Route path="task/list" element={<PermissionGuard privilege="TaskView" module="nerve" routeLabel="Task Ledger"><TaskLedgerScreen /></PermissionGuard>} />
                  <Route path="task/entry" element={<PermissionGuard anyOf={['TaskAdd', 'TaskEdit']} module="nerve" routeLabel="Task Configuration"><TaskConfigurationScreen /></PermissionGuard>} />
                  <Route path="task/bulk-upload" element={<PermissionGuard privilege="TaskAdd" module="nerve" routeLabel="Task Bulk Upload"><TaskBulkUploadScreen /></PermissionGuard>} />

                  <Route path="job/list" element={<PermissionGuard privilege="JobView" module="nerve" routeLabel="Job Inventory"><JobInventoryListScreen /></PermissionGuard>} />
                  <Route path="job/entry" element={<PermissionGuard anyOf={['JobAdd', 'JobEdit']} module="nerve" routeLabel="Job Configuration"><JobConfigurationScreen /></PermissionGuard>} />

                  <Route path="timesheet/list" element={<PermissionGuard privilege="TimesheetView" module="nerve" routeLabel="Timesheet"><TimesheetListScreen /></PermissionGuard>} />
                  <Route path="timesheet/entry" element={<PermissionGuard anyOf={['TimesheetAdd', 'TimesheetEdit']} module="nerve" routeLabel="Timesheet Entry"><TimesheetEntryScreen /></PermissionGuard>} />

                  <Route path="ticketsync/list" element={<PermissionGuard privilege="TicketSyncView" module="nerve" routeLabel="Ticket Integration"><TicketInflowRegistryScreen /></PermissionGuard>} />
                </Route>
                <Route path="project-management" element={<SectionIndexRedirect menuId="nerve" />} />
                <Route path="project-management/*" element={<LegacyRedirect to="/nerve" />} />

                {/* ── Resource Allocation ── */}
                <Route path="allocation">
                  <Route index element={<Navigate to="board" replace />} />
                  <Route
                    path="board"
                    element={
                      <PermissionGuard privilege="ResourceAllocationView" module="nerve" routeLabel="Resource Allocation">
                        <ResourceAllocationBoardScreen />
                      </PermissionGuard>
                    }
                  />
                </Route>

                {/* ── Support ── */}
                <Route path="support">
                  <Route index element={<Navigate to="tickets/list" replace />} />
                  <Route path="tickets/list" element={<SupportTicketManagementScreen />} />
                  <Route path="tickets/entry" element={<SupportTicketEntryScreen />} />
                </Route>

                {/* ── Approvals ── */}
                <Route path="approval">
                  <Route index element={<SectionIndexRedirect menuId="approval" />} />
                  <Route path="company/list" element={<Navigate to="/approval/masters/company" replace />} />
                  <Route path="masters/list" element={<PermissionGuard privilege="ApprovalMastersView" routeLabel="Masters Approval"><MasterApprovalScreen /></PermissionGuard>} />
                  <Route path="masters/company" element={<PermissionGuard anyOf={['ApprovalCompanyView', 'ApprovalMastersView']} routeLabel="Company Approval"><CompanyApprovalScreen /></PermissionGuard>} />
                  <Route path="timesheet/list" element={<PermissionGuard privilege="ApprovalTimesheetView" module="nerve" routeLabel="Timesheet Approval"><TimesheetApprovalScreen /></PermissionGuard>} />
                  <Route path="leave/list" element={<PermissionGuard privilege="ApprovalLeaveView" module="bruno" routeLabel="Leave Approval"><LeaveApprovalScreen /></PermissionGuard>} />
                  <Route path="productivity-hours/list" element={<PermissionGuard privilege="ApprovalProductivityHoursView" module="bruno" routeLabel="Productivity Hours Approval"><ProductivityAdjustmentApprovalScreen /></PermissionGuard>} />
                  <Route path="productivity-hours/view" element={<PermissionGuard privilege="ApprovalProductivityHoursView" module="bruno" routeLabel="Productivity Hours Approval Details"><ProductivityAdjustmentApprovalDetailScreen /></PermissionGuard>} />
                </Route>

                {/* ── Analytics ── */}
                <Route path="analytics">
                  <Route index element={<SectionIndexRedirect menuId="analytics" />} />
                  <Route
                    path=":module"
                    element={
                      <PermissionGuard
                        anyOf={['AnalyticsUserView', 'AnalyticsProjectView', 'AnalyticsTaskView', 'AnalyticsJobView']}
                        module="analytics"
                        routeLabel="Analytics"
                      >
                        <AnalyticsScreen />
                      </PermissionGuard>
                    }
                  />
                </Route>

                {/* ── Bridge (Communication) ── */}
                <Route path="bridge">
                  <Route index element={<Navigate to="hub" replace />} />
                  <Route path=":module" element={<PermissionGuard module="bridge" routeLabel="Communication"><CommunicationRouteWrapper /></PermissionGuard>} />
                </Route>
                <Route path="communication" element={<Navigate to="/bridge/hub" replace />} />
                <Route path="communication/*" element={<LegacyRedirect to="/bridge" />} />

                {/* ── Loop (Telephony) ── */}
                <Route path="loop">
                  <Route index element={<Navigate to="call-center" replace />} />
                  <Route path="call-center" element={<PermissionGuard module="telephony" routeLabel="Call Center"><CallCenterScreen /></PermissionGuard>} />
                  <Route path="call-logs" element={<PermissionGuard module="telephony" routeLabel="Call Logs"><CallLogsScreen /></PermissionGuard>} />
                  <Route path="settings" element={<PermissionGuard module="telephony" routeLabel="Telephony Settings"><TelephonySettingsScreen /></PermissionGuard>} />
                </Route>
                <Route path="telephony" element={<Navigate to="/loop/call-center" replace />} />
                <Route path="telephony/*" element={<LegacyRedirect to="/loop" />} />

                {/* ── Reports ── */}
                <Route path="reports">
                  <Route index element={<SectionIndexRedirect menuId="reports" />} />
                  <Route path="schedules/list" element={<PermissionGuard privilege="ReportsAllView" routeLabel="Report Schedules"><ReportSchedulesListScreen /></PermissionGuard>} />
                  <Route path="schedules/entry" element={<PermissionGuard privilege="ReportsAllView" routeLabel="Report Schedule Entry"><ReportScheduleEntryScreen /></PermissionGuard>} />
                  <Route path=":module" element={<PermissionGuard anyOf={['ReportsAllView', 'ProjectView', 'TaskView', 'TimesheetView', 'CompanyView', 'DashboardProductivityView']} routeLabel="Reports"><ReportsScreen /></PermissionGuard>} />
                </Route>

                {/* ── Notifications ── */}
                <Route path="notifications" element={<NotificationScreen />} />

                {/* ── Audit Logs ── */}
                <Route
                  path="audit-logs"
                  element={
                    <PermissionGuard
                      anyOf={['AuditLogsView', 'ReportsAllView', 'ProjectView', 'TaskView', 'JobView', 'TimesheetView']}
                      routeLabel="Audit Logs"
                    >
                      <AuditLogScreen />
                    </PermissionGuard>
                  }
                />

                {/* ── Email & Notification Settings ── */}
                <Route
                  path="settings/email-notifications"
                  element={<Navigate to="/settings/email-notifications/workflows" replace />}
                />
                <Route
                  path="settings/email-notifications/:tab"
                  element={<EmailNotificationSettingsScreen />}
                />

                {/* ── Employee Self Service (ESS) ── */}
                <Route path="self-service">
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<EmployeeDashboardScreen />} />
                  <Route path="my-profile" element={<MyProfileScreen />} />
                  <Route path="company-policy" element={<CompanyPolicyScreen />} />
                  <Route path="attendance-history" element={<ESSAttendanceHistoryListScreen />} />
                  <Route path="attendance-regularization" element={<AttendanceRegularizationListScreen />} />
                  <Route path="attendance-regularization/list" element={<AttendanceRegularizationListScreen />} />
                  <Route path="attendance-regularization/entry" element={<AttendanceRegularizationEntryScreen />} />
                  <Route path="attendance-regularization/entry/:id" element={<AttendanceRegularizationEntryScreen />} />
                  <Route path="holiday" element={<ESSHolidayListScreen />} />
                  <Route path="holiday/list" element={<ESSHolidayListScreen />} />
                  <Route path="holiday/entry" element={<ESSHolidayListScreen />} />
                  <Route path="leave-balance" element={<LeaveBalanceListScreen />} />
                  <Route path="leave-apply" element={<LeaveApplyListScreen />} />
                  <Route path="leave-apply/list" element={<LeaveApplyListScreen />} />
                  <Route path="leave-apply/entry" element={<LeaveApplyEntryScreen />} />
                  <Route path="leave-apply/entry/:id" element={<LeaveApplyEntryScreen />} />
                  <Route path="loan-application" element={<LoanApplicationListScreen />} />
                  <Route path="loan-application/list" element={<LoanApplicationListScreen />} />
                  <Route path="loan-application/entry" element={<LoanApplicationEntryScreen />} />
                  <Route path="loan-application/entry/:id" element={<LoanApplicationEntryScreen />} />
                  <Route path="payslip" element={<PayslipListScreen />} />
                  <Route path="emp-incentives" element={<EmpIncentivesListScreen />} />
                  <Route path="my-team" element={<MyTeamListScreen />} />
                  <Route path="my-subordinates" element={<MySubordinatesListScreen />} />
                  <Route path="search-employee" element={<SearchEmployeeListScreen />} />
                </Route>
              </Route>

              {/* ── Fallback ── */}
              <Route path="*" element={<Navigate to="/dashboard/personal" replace />} />
            </Routes>
          </BrowserRouter>
        </AppTooltipProvider>
      </AppNotificationBusProvider>
      {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> */}
    </QueryClientProvider>
  )
}

export default App
